import { useEffect, useState } from 'react'
import { Bell, CheckCircle2, MapPin, User } from 'lucide-react'
import { useScrollReveal } from '../../hooks/useScrollReveal.js'
import InputField from '../ui/InputField.jsx'
import SelectField from '../ui/SelectField.jsx'
import Button from '../ui/Button.jsx'
import { registerWaitlist } from '../../services/waitlistApi.js'

/* ─────────────────────────────────────────────────────────────
   Constants
   ───────────────────────────────────────────────────────────── */

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  area: '',
  interestType: 'BUYER',
}

const STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
})

const FIELD_MAX_LENGTHS = {
  name: 100,
  email: 254,
  phone: 20,
}

const NAME_MIN_LENGTH = 2
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_RE = /^\+?[1-9]\d{6,14}$/

/* ─────────────────────────────────────────────────────────────
   Input sanitisation
   ───────────────────────────────────────────────────────────── */

function sanitiseText(str, { trim = false } = {}) {
  const cleaned = str.replace(/<[^>]*>/g, '').replace(/\0/g, '')
  return trim ? cleaned.trim() : cleaned
}

function sanitisePhone(str) {
  return str.replace(/[^\d+]/g, '')
}

function sanitiseField(name, value, { trim = false } = {}) {
  if (name === 'phone') return sanitisePhone(value)
  if (name === 'interestType') return value
  return sanitiseText(value, { trim })
}

/* ─────────────────────────────────────────────────────────────
   Validation
   ───────────────────────────────────────────────────────────── */

function validate(values) {
  const errors = {}

  if (!values.name) {
    errors.name = 'Your name is required.'
  } else if (values.name.length < NAME_MIN_LENGTH) {
    errors.name = 'At least 2 characters.'
  } else if (values.name.length > FIELD_MAX_LENGTHS.name) {
    errors.name = `Max ${FIELD_MAX_LENGTHS.name} characters.`
  }

  if (!values.email) {
    errors.email = 'Email is required.'
  } else if (!EMAIL_RE.test(values.email)) {
    errors.email = 'Enter a valid email address.'
  } else if (values.email.length > FIELD_MAX_LENGTHS.email) {
    errors.email = `Max ${FIELD_MAX_LENGTHS.email} characters.`
  }

  // Phone is optional — only validate when provided
  if (values.phone && !PHONE_RE.test(values.phone)) {
    errors.phone = 'Enter a valid number, e.g. +233201234567.'
  }

  return errors
}

/* ─────────────────────────────────────────────────────────────
   Component
   ───────────────────────────────────────────────────────────── */

export default function Waitlist() {
  const leftRef = useScrollReveal()
  const rightRef = useScrollReveal({ threshold: 0.1 })

  const [values, setValues] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [status, setStatus] = useState(STATUS.IDLE)
  const [apiError, setApiError] = useState('')
  const [areaOptions, setAreaOptions] = useState([
    { value: '', label: 'Select your area (optional)' },
  ])
  const [alreadyRegistered, setAlreadyRegistered] = useState(false)

  // Fetch areas from backend (same list as merchant form, mobile app, etc.)
  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
    let cancelled = false

    fetch(`${API_BASE}/api/areas/`)
      .then((r) => r.ok ? r.json() : [])
      .then((areas) => {
        if (cancelled || !Array.isArray(areas)) return
        setAreaOptions([
          { value: '', label: 'Select your area (optional)' },
          ...areas.map((a) => ({ value: a, label: a })),
        ])
      })
      .catch(() => { /* keep default option */ })

    return () => { cancelled = true }
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    const newVal = sanitiseField(name, value)
    setValues((prev) => ({ ...prev, [name]: newVal }))
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validate({ ...values, [name]: newVal })[name],
      }))
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    const trimmed = sanitiseField(name, value, { trim: true })
    setValues((prev) => ({ ...prev, [name]: trimmed }))
    setTouched((prev) => ({ ...prev, [name]: true }))
    setErrors((prev) => ({ ...prev, [name]: validate({ ...values, [name]: trimmed })[name] }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const allTouched = Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, true]))
    setTouched(allTouched)

    const validationErrors = validate(values)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    setStatus(STATUS.LOADING)
    setApiError('')

    const result = await registerWaitlist({
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      phone: values.phone,
      area: values.area,
      interestType: values.interestType,
      source: 'website',
    })

    if (result.ok) {
      setAlreadyRegistered(Boolean(result.alreadyRegistered))
      setStatus(STATUS.SUCCESS)
      setValues(EMPTY_FORM)
      setTouched({})
      setErrors({})
    } else {
      setStatus(STATUS.ERROR)
      if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...result.fieldErrors }))
        setTouched((prev) => ({
          ...prev,
          ...Object.fromEntries(Object.keys(result.fieldErrors).map((k) => [k, true])),
        }))
      }
      setApiError(result.generalError ?? 'Something went wrong. Please try again.')
    }
  }

  /* ── Success state ─────────────────────────────────────── */
  if (status === STATUS.SUCCESS) {
    return (
      <section
        id="waitlist"
        className="py-24 bg-gradient-to-br from-primary-light via-white to-white"
      >
        <div className="section-container">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-primary-light flex items-center
                            justify-center mx-auto mb-6 animate-[scale-in_0.4s_ease-out]">
              <CheckCircle2 className="w-8 h-8 text-primary" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-extrabold text-brand-dark mb-3">
              {alreadyRegistered ? "You're already on the list!" : "You're on the list!"}
            </h2>
            <p className="text-base text-brand-gray leading-relaxed">
              {alreadyRegistered
                ? "We've already got your details. We'll notify you the moment DecidA launches in Accra."
                : "Thanks for joining the waitlist. We'll notify you the moment DecidA launches in Accra."}
            </p>
            <button
              onClick={() => {
                setStatus(STATUS.IDLE)
                setAlreadyRegistered(false)
              }}
              className="mt-8 text-sm font-semibold text-primary hover:underline"
            >
              Add another email
            </button>
          </div>
        </div>
      </section>
    )
  }

  const isLoading = status === STATUS.LOADING

  return (
    <section
      id="waitlist"
      className="py-24 bg-gradient-to-br from-primary-light via-white to-white relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full
                        bg-gradient-to-br from-primary/10 to-transparent blur-[100px]" />
      </div>

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* ── Left: pitch copy ─────────────────────── */}
          <div ref={leftRef} className="reveal-left lg:sticky lg:top-24">
            <p className="eyebrow mb-4 justify-center sm:justify-start">
              <span className="w-5 h-px bg-primary" />
              Be First in Line
            </p>
            <h2
              className="font-extrabold text-brand-dark mb-5"
              style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.75rem)' }}
            >
              Be the first to know when{' '}
              <span className="text-primary">DecidA launches in Accra.</span>
            </h2>
            <p className="text-base text-brand-gray leading-relaxed mb-8 max-w-md">
              We&apos;re launching soon. Drop your details below and we&apos;ll notify you the
              moment the apps are live, so you can start getting offers from local
              providers right away.
            </p>

            <ul className="flex flex-col gap-4" role="list">
              {[
                { icon: Bell, text: 'Be among the first to use DecidA when we launch.' },
                { icon: MapPin, text: 'Tell us your area so we can prioritise nearby providers.' },
                { icon: User, text: '100% free. We’ll never spam, one launch notification only.' },
              ].map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 text-sm font-medium text-brand-dark"
                >
                  <div className="w-7 h-7 rounded-lg bg-primary-light flex items-center
                                  justify-center flex-shrink-0 mt-px">
                    <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
                  </div>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Right: form card ─────────────────────── */}
          <div ref={rightRef} className="reveal-right card-elevated p-8 md:p-10">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-brand-dark">
                Join the Waitlist
              </h3>
            </div>

            <form
              onSubmit={handleSubmit}
              noValidate
              autoComplete="off"
              className="flex flex-col gap-5"
            >
              <InputField
                label="Full Name"
                id="waitlist-name"
                name="name"
                type="text"
                placeholder="e.g. Awurama Mensah"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name ? errors.name : undefined}
                maxLength={FIELD_MAX_LENGTHS.name}
                required
                disabled={isLoading}
                autoComplete="name"
              />

              <InputField
                label="Email Address"
                id="waitlist-email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email ? errors.email : undefined}
                maxLength={FIELD_MAX_LENGTHS.email}
                required
                disabled={isLoading}
                autoComplete="email"
              />

              <div>
                <label htmlFor="waitlist-phone"
                  className="text-sm font-semibold text-brand-dark block mb-1">
                  Phone Number <span className="text-brand-gray font-normal">(optional)</span>
                </label>
                <div className="flex items-stretch">
                  <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0
                                   border-gray-200 bg-gray-50 text-sm font-semibold text-brand-dark
                                   select-none flex-shrink-0">
                    +
                  </span>
                  <input
                    id="waitlist-phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="233201234567"
                    value={values.phone.replace(/^\+/, '')}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '')
                      handleChange({ target: { name: 'phone', value: digits ? '+' + digits : '' } })
                    }}
                    onBlur={handleBlur}
                    maxLength={15}
                    disabled={isLoading}
                    autoComplete="tel"
                    className={`form-field rounded-l-none flex-1 ${touched.phone && errors.phone ? 'form-field-error' : ''
                      }`}
                  />
                </div>
                {touched.phone && errors.phone && (
                  <p className="text-xs text-red-500 font-medium mt-0.5">{errors.phone}</p>
                )}
                <p className="mt-1.5 text-xs text-brand-gray">
                  We&apos;ll notify you when DecidA launches.
                </p>
              </div>

              <SelectField
                label="Area in Accra"
                id="waitlist-area"
                name="area"
                options={areaOptions}
                value={values.area}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.area ? errors.area : undefined}
                disabled={isLoading}
              />

              {status === STATUS.ERROR && apiError && (
                <div
                  role="alert"
                  className="rounded-xl bg-red-50 border border-red-200 px-4 py-3"
                >
                  <p className="text-sm text-red-600 font-medium">{apiError}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="btn-shimmer w-full !py-4 !text-[15px] mt-1"
                disabled={isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Submitting&hellip;
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" aria-hidden="true" />
                    Join the Waitlist
                  </>
                )}
              </Button>

              <p className="text-xs text-brand-gray text-center">
                By joining, you agree to receive a single launch notification.
                We&apos;ll never share your details.
              </p>

            </form>
          </div>

        </div>
      </div>
    </section>
  )
}
